import React, { type ReactElement } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import Alert from 'atom-components/alert';
import SystemErrorAlert from 'feature-components/system-error-alert';

// Query result from a useOne or useMany hook. Each query result can contain an error message.
type QueryResult =
  | UseQueryResult<RequestResponse<unknown[]>, RequestError> // Support useMany errors
  | UseQueryResult<RequestResponse<unknown>, RequestError>; // Support useOne errors

type Props = {
  queries: QueryResult[];
};

/**
 * An error alert that displays a list of errors from multiple query failures. Currently does
 * not support useAuth and useAuthCustomer hooks.
 */
export default function LoadingErrorAlert({ queries }: Props): ReactElement {
  const queriesWithError = queries.filter((query) => query.error);

  // If this system error occurs it could mean that the query that had an error was not passed to
  // the loading error alert component (for example, by the developer forgetting to pass it as
  // part of the queries array).
  if (queriesWithError.length === 0)
    return (
      <SystemErrorAlert>
        An error occurred while loading data, but no particular error messages were found.
      </SystemErrorAlert>
    );

  return (
    <Alert variant="danger">
      <p>The following errors occurred while loading data:</p>
      <ul>
        {queries.map((query) => (
          <li>{query.error?.message || 'No error message found.'}</li>
        ))}
      </ul>
    </Alert>
  );
}
